import type { Severity } from './diagnostic';

/**
 * The complete taxonomy of things we are willing to say about a document.
 *
 * Codes are namespaced strings rather than a numeric enum on purpose (ADR-0008): they are
 * stable across refactors, readable in snapshots and bug reports, usable directly as i18n
 * keys, and they survive `postMessage` without both sides sharing an enum table.
 *
 * Adding a code is a three-file change by construction: this union, the severity table
 * below, and `core/messages/en.ts` — which is typed as an exhaustive
 * `Record<MessageCode, string>`, so a forgotten template is a compile error rather than a
 * raw code shown to a user.
 */
export type DiagnosticCode =
  | 'syntax/unexpected-token'
  | 'syntax/value-expected'
  | 'syntax/property-name-expected'
  | 'syntax/colon-expected'
  | 'syntax/comma-expected'
  | 'syntax/unexpected-comma'
  | 'syntax/trailing-comma'
  | 'syntax/close-brace-expected'
  | 'syntax/close-bracket-expected'
  | 'syntax/unexpected-eof'
  | 'syntax/trailing-content'
  | 'syntax/empty-document'
  | 'string/unterminated'
  | 'string/invalid-escape'
  | 'string/invalid-unicode-escape'
  | 'string/control-character'
  | 'string/single-quoted'
  | 'string/smart-quoted'
  | 'number/invalid-literal'
  | 'number/leading-zero'
  | 'number/leading-plus'
  | 'number/leading-decimal-point'
  | 'number/trailing-decimal-point'
  | 'number/incomplete-exponent'
  | 'number/hex-literal'
  | 'number/non-finite'
  | 'number/precision-loss'
  | 'key/unquoted'
  | 'literal/js-undefined'
  | 'literal/python-keyword'
  | 'jsonc/comment'
  | 'semantic/duplicate-key'
  | 'semantic/deep-nesting'
  | 'encoding/bom'
  | 'internal/analysis-aborted';

/**
 * Severity per code. A check EMITS A DRAFT and never names a severity; the engine reads it
 * from here (ADR-0008). One table, one place to argue about strictness — and the single
 * substitution point a future `jsonc` / `permissive` profile would need
 * (docs/ARCHITECTURE.md §9).
 *
 * `error` means "this is not JSON": `format()` refuses to run and the pill is red.
 * `warning` means "this is JSON, and you probably did not mean it".
 */
export const DEFAULT_SEVERITY: Readonly<Record<DiagnosticCode, Severity>> = {
  'syntax/unexpected-token': 'error',
  'syntax/value-expected': 'error',
  'syntax/property-name-expected': 'error',
  'syntax/colon-expected': 'error',
  'syntax/comma-expected': 'error',
  'syntax/unexpected-comma': 'error',
  'syntax/trailing-comma': 'error',
  'syntax/close-brace-expected': 'error',
  'syntax/close-bracket-expected': 'error',
  'syntax/unexpected-eof': 'error',
  'syntax/trailing-content': 'error',
  // Not an error: an empty editor is the state the application starts in, and a red pill
  // there would be a lie. `beautify()` handles "nothing to format" on its own path.
  'syntax/empty-document': 'info',
  'string/unterminated': 'error',
  'string/invalid-escape': 'error',
  'string/invalid-unicode-escape': 'error',
  'string/control-character': 'error',
  'string/single-quoted': 'error',
  'string/smart-quoted': 'error',
  'number/invalid-literal': 'error',
  'number/leading-zero': 'error',
  'number/leading-plus': 'error',
  'number/leading-decimal-point': 'error',
  'number/trailing-decimal-point': 'error',
  'number/incomplete-exponent': 'error',
  'number/hex-literal': 'error',
  'number/non-finite': 'error',
  // Valid JSON that a double cannot hold exactly. Reporting it as an error would refuse to
  // format a document that is not broken.
  'number/precision-loss': 'warning',
  'key/unquoted': 'error',
  'literal/js-undefined': 'error',
  'literal/python-keyword': 'error',
  'jsonc/comment': 'error',
  // Which of the two values to drop is a decision about DATA, not about syntax, so this is
  // reported with both locations and never repaired (docs/ARCHITECTURE.md §9).
  'semantic/duplicate-key': 'warning',
  'semantic/deep-nesting': 'warning',
  // A leading BOM makes `JSON.parse` throw, so this really is invalid input — but it is
  // repaired by a `safe` rule, which is why the user sees "fix, then format".
  'encoding/bom': 'error',
  'internal/analysis-aborted': 'error',
};

/** Every code, in declaration order. Derived from the table so the two cannot drift. */
export const ALL_DIAGNOSTIC_CODES = Object.keys(DEFAULT_SEVERITY) as readonly DiagnosticCode[];

/** Severity for `code`, from the default table. */
export function severityOf(code: DiagnosticCode): Severity {
  return DEFAULT_SEVERITY[code];
}
